import { shuffle } from "lodash";
import {
  BigNumber,
  format,
  isBigNumber,
  MathJsStatic,
  MathNode,
  typed
} from "mathjs";

import { InputTypes } from "../templating/types";
import { areValuesEqual } from "./areValuesEqual";
import { SUPPORTED_CHARACTERS } from "./consts";
import { funcEqual } from "./exprEquality/funcEqual";
import { structEqual } from "./exprEquality/structEqual";
import {
  add,
  constant,
  divide,
  equalUnordered,
  expand,
  multiply,
  parseExpr,
  pow,
  sqrt,
  subtract,
  truncate
} from "./operations";

export type MathPrimitive = string | BigNumber | MathNode;

export function extendMath(math: MathJsStatic) {
  // @ts-ignore
  math.typed.addType({
    name: "Node",
    test: isMathNode
  });

  math.config({
    number: "BigNumber",
    precision: 64, // Number of significant digits for BigNumbers
    epsilon: 1e-60
  });

  // Add unsupported characters into the parser
  const isAlphaOriginal = math.parse.isAlpha;
  math.parse.isAlpha = (c, cPrev, cNext) => {
    return isAlphaOriginal(c, cPrev, cNext) || SUPPORTED_CHARACTERS.includes(c);
  };

  math.import(
    {
      truncate: math.typed("truncate", {
        "BigNumber, BigNumber": function (a, b) {
          return truncate(a, b);
        },
        BigNumber: function (a) {
          return truncate(math.number(a));
        }
      }),
      formatSig: math.typed("formatSig", {
        "BigNumber, BigNumber": (x: number, count: number) =>
          math.number(x).toPrecision(count)
      }),
      roundSig: math.typed("roundSig", {
        "BigNumber, BigNumber": (x: number, count: number) =>
          math.bignumber(math.number(x).toPrecision(count))
      }),
      divide: typed("divide", {
        "Node, Node": divide
      }),
      multiply: typed("multiply", {
        "Node, Node": multiply
      }),
      subtract: typed("subtract", {
        "Node, Node": subtract
      }),
      add: typed("add", {
        "Node, Node": add
      }),
      pow: typed("pow", {
        "Node, Node": pow
      }),
      sqrt: typed("sqrt", {
        Node: sqrt
      }),
      getCount: typed("getCount", {
        string: (a: string) => a.length,
        number: (a: number) => a.toString().length,
        BigNumber: (a: BigNumber) => a.toString().length,
        Array: a => a.length
      }),
      expr: typed("expr", {
        string: parseExpr,
        number: constant,
        BigNumber: constant
      }),
      expand: typed("expand", {
        Node: expand
      }),
      funcEqual: typed("funcEqual", {
        "Node, Node": funcEqual
      }),
      structEqual: typed("structEqual", {
        "Node, Node": structEqual
      }),
      equal: typed("equal", {
        "string, string": (a: string, b: string) => areValuesEqual(a, b)
      }),
      equalUnordered: typed("equalUnordered", {
        "Array, Array": equalUnordered
      }),
      fixedDecimalPlaces: typed("fixedDecimalPlaces", {
        "BigNumber, BigNumber": (a, decimals) =>
          math.number(a).toFixed(math.number(decimals))
      }),
      format: typed("format", {
        "BigNumber | string | number, number": (value, opt) =>
          format(value, opt),
        "BigNumber | string | number, BigNumber": (value, opt) =>
          format(value, math.number(opt)),
        "BigNumber | string | number, Object": (value, opt) => {
          Object.entries(opt).forEach(([key, value]) => {
            if (isBigNumber(value)) {
              opt[key] = math.number(value);
            }
          });
          return format(value, opt);
        }
      }),
      selectInput: typed("selectInput", {
        "string | BigNumber | Node, Array": (correctValue, options) => ({
          inputType: InputTypes.SELECT,
          correctValue,
          randomisedOptions: shuffle([correctValue, ...options])
        })
      })
    },
    {}
  );
}

export function isMathNode(value: any): value is MathNode {
  return !!value?.isNode;
}
